import React, { useState, useEffect } from "react";
import { useForm, useFieldArray, FormProvider } from "react-hook-form";
import { Link as RouterLink, useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { getJobOrder, putJobOrder, patchJobOrderLink } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";
import { sendEmailQueue } from "../API/emailAPI";

import TermiteLocationCard from "./FormComponents/TermiteLocationCard";
import ExterraCard from "./FormComponents/ExterraCard";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";
import PageOneReviewSection from "./FormComponents/PageOneReviewSection";
import PaymentDetailsSection from "./FormComponents/PaymentDetailsSection";
import DocumentLinksCard from "./FormComponents/DocumentLinksCard";
import TechnicianOnlyReviewSection from "./FormComponents/TechnicianOnlyReviewSection";
import ClientOnlyReviewSection from "./FormComponents/ClientOnlyReviewSection";
import SectionTimestampNotice from "./FormComponents/SectionTimestampNotice";
import { buildGeneratedDocumentLinks } from "./FormComponents/email/buildGeneratedDocumentLinks";
import { buildServiceReportEmail } from "./FormComponents/email/buildServiceReportEmail";
import { getEffectiveEndTime } from "./utils/timeFormatter";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function EditTermiteControlForm() {
  const { id } = useParams();
  const location = useLocation();
  const [jobOrder, setJobOrder] = useState({});
  const [client, setClient] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [sendEmailAfterEdit, setSendEmailAfterEdit] = useState(true);

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  const navigate = useNavigate();

  // Get returnTo parameter
  const returnTo = new URLSearchParams(location.search).get('returnTo');

  // RHF: form state for locations & exterra stations
  const methods = useForm({
    mode: "onChange",
    shouldUnregister: false,
    defaultValues: {
      locations: [],
      exterra_bait_stations: [],
      termite_recommendations: [],
    },
  });
  const { control, register, setValue, getValues } = methods;

  // Field array for Location cards
  const {
    fields: locationFields,
    append: appendLocation,
    remove: removeLocation,
    replace: replaceLocations,
  } = useFieldArray({ control, name: "locations" });


  useEffect(() => {
  const fetchJobOrder = async () => {
    try {
      setIsLoading(true);

      const data = await getJobOrder(id);
      const clientData = await getClient(data.client_id);
      setClient(clientData || {});
      setJobOrder(data || {});
      
    } catch (err) {
      setErrorMessage("Error fetching job order: " + err.message); 
    } finally {
      setIsLoading(false);
    }
  };

  if (id) { // Only fetch if id exists
    fetchJobOrder();
  }
}, [id]);

  // helper to normalize labels into keys
  const formatKey = (s) => s.toLowerCase().replace(/\s+/g, "_");

  // map raw keys back to label arrays and sort alphabetically
  const displayedJobTypes = JOB_TYPES
    .filter((label) => (jobOrder.job_types || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const displayedJobDescriptions = JOB_DESCRIPTIONS
    .filter((label) => (jobOrder.job_description || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));
  
  const STRUCTURE_TYPE_OPTIONS = [
    { key: "residential", label: "Residential" },
    { key: "commercial",  label: "Commercial"  },
    { key: "industrial",  label: "Industrial"  },
  ];

  // Normalize truthy values (true / "true" / 1 / "yes", etc.)
  const isTrue = (v) =>
    typeof v === "boolean" ? v
    : typeof v === "number" ? v === 1
    : typeof v === "string" ? ["true","1","yes","y","on"].includes(v.trim().toLowerCase())
    : false;

  const sd = jobOrder?.structure_details ?? {};

  const normalize = (v) => String(v ?? "").trim().toLowerCase();

  const ta = normalize(jobOrder?.termite_activity);

  // helper: parse action_taken "Baiting System Installation/ AG Monitoring in ___"
  const decodeActionTaken = (s = "") => {
    const t = s.trim();
    if (!t) return { action_taken: "", bait_monitor_location: "" };
    if (t.startsWith("Baiting System Installation/ AG Monitoring in")) {
      return {
        action_taken: "baiting",
        bait_monitor_location: t.replace("Baiting System Installation/ AG Monitoring in", "").trim(),
      };
    }
    const map = {
      "Soil Treatment": "soil",
      "Spraying": "spraying",
      "Monitoring only": "monitoring",
    };
    return { action_taken: map[t] || "", bait_monitor_location: "" };
  };

  const primedRef = React.useRef(false);

  const LOCATION_OPTIONS_SET = new Set([
    "Living Room / Sala",
    "Bedroom 1",
    "Bedroom 2",
    "Bedroom 3",
    "Kitchen",
    "Dining Area",
    "Bathroom 1",
    "Bathroom 2",
    "Closet / Cabinets",
    "Hallways",
    "Staircase",
    "Ceiling",
    "Baseboard",
    "Floor",
    "Exterior Perimeter",
    "Garage",
    "Garden",
    "Storage Area",
    "Other",
  ]);


  // Prime RHF cards whenever the fetched job order changes
  useEffect(() => {
    if (!jobOrder?.id || primedRef.current) return;

    // Locations → RHF

    const mappedLocations = (jobOrder?.inspection_report || [])
      .filter(r => r && (r.location || r.findings || r.action_taken || r.remarks))
      .map(r => {
        const { action_taken, bait_monitor_location } =
          decodeActionTaken(r.action_taken || "");

        const rawLoc = (r.location || "").trim();
        const rawOther = (r.location_other || "").trim();

        const isLegacyCustom =
          rawLoc && !LOCATION_OPTIONS_SET.has(rawLoc);

        const uiLocation =
          rawOther ? "Other" : (isLegacyCustom ? "Other" : rawLoc);

        const uiLocationOther =
          rawOther ? rawOther : (isLegacyCustom ? rawLoc : "");

        return {
          location: uiLocation,
          location_other: uiLocationOther,
          findings: r.findings || "",
          action_taken,
          bait_monitor_location,
          remarks: r.remarks || "",
        };
      });


    // Exterra → RHF
    const mappedExterra = Array.isArray(jobOrder?.exterra_bait_stations)
      ? jobOrder.exterra_bait_stations
      : [];

    // Prime field-array and other values once
    replaceLocations(mappedLocations.length ? mappedLocations : []);
    setValue("exterra_bait_stations", mappedExterra, { shouldDirty: false });
    setValue("termite_recommendations", jobOrder?.termite_recommendations || [], { shouldDirty: false });

    primedRef.current = true; // prevent future overwrites while user is typing
  }, [jobOrder, replaceLocations, setValue]);

  // Encode back to the persisted "action_taken" string
  const encodeActionTaken = ({ action_taken, bait_monitor_location }) => {
    if (action_taken === "baiting") {
      return `Baiting System Installation/ AG Monitoring in ${bait_monitor_location || ""}`.trim();
    }
    const map = { soil: "Soil Treatment", spraying: "Spraying", monitoring: "Monitoring only" };
    return map[action_taken] || "";
  };

  // --- Payment defaults (keep order stable)
  const DEFAULT_PAYMENT_STATUS = [
    { label: "Paid", selection: false },
    { label: "COD", selection: false },
    { label: "For Collection", selection: false },
  ];

  const DEFAULT_PAYMENT_METHOD = [
    { label: "Cash",  selection: false },
    { label: "Check", selection: false },
    { label: "Free",  selection: false },
  ];

  // Local UI state (what the table renders)
  const [service_report_payment_status, setServiceReportPaymentStatus] = useState(DEFAULT_PAYMENT_STATUS);
  const [service_report_payment_method, setServiceReportPaymentMethod] = useState(DEFAULT_PAYMENT_METHOD);

  // Normalize incoming array/string → align with our defaults by label
  const normalizeByLabel = (incoming, defaults) => {
    if (Array.isArray(incoming)) {
      const map = new Map(incoming.map(i => [String(i.label || i.value).toLowerCase(), !!i.selection]));
      return defaults.map(d => ({ ...d, selection: !!map.get(d.label.toLowerCase()) }));
    }
    if (typeof incoming === "string") {
      const v = incoming.toLowerCase();
      return defaults.map(d => ({ ...d, selection: d.label.toLowerCase() === v }));
    }
    return defaults;
  };

  useEffect(() => {
    const status = normalizeByLabel(jobOrder?.service_report_payment_status, DEFAULT_PAYMENT_STATUS);
    const method = normalizeByLabel(jobOrder?.service_report_payment_method, DEFAULT_PAYMENT_METHOD);

    setServiceReportPaymentStatus(status);
    setServiceReportPaymentMethod(method);

    // keep outgoing payload in sync
    setJobOrder(prev => ({
      ...prev,
      service_report_payment_status: status,
      service_report_payment_method: method,
    }));
  }, [jobOrder?.service_report_payment_status, jobOrder?.service_report_payment_method]);

  const handlePaymentStatusChange = (statusIndex) => {
    const next = service_report_payment_status.map((s, i) => ({
      ...s,
      selection: i === statusIndex ? !s.selection : false,
    }));
    setServiceReportPaymentStatus(next);
    setJobOrder(prev => ({ ...prev, service_report_payment_status: next }));
  };

  const handlePaymentMethodChange = (methodIndex) => {
    const next = service_report_payment_method.map((m, i) => ({
      ...m,
      selection: i === methodIndex ? !m.selection : false,
    }));
    setServiceReportPaymentMethod(next);
    setJobOrder(prev => ({ ...prev, service_report_payment_method: next }));
  };

  const handleInputChange = (field, value) => {
   setJobOrder(prev => ({ ...prev, [field]: value }));
  };

  // Handler for nested object updates
  const handleNestedChange = (parentField, field, value) => {
    setJobOrder(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [field]: value
      }
    }));
  };

  const buildClientSignatureIsoFromEditedTime = (hhmm) => {
    if (!hhmm) return jobOrder?.client_signature_uploaded_at || "";

    const [hour, minute] = String(hhmm).split(":").map(Number);

    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return jobOrder?.client_signature_uploaded_at || "";
    }

    try {
      const baseIso = jobOrder?.client_signature_uploaded_at;
      const base = baseIso
        ? new Date(baseIso)
        : new Date(`${jobOrder?.job_date}T00:00:00+08:00`);

      if (Number.isNaN(base.getTime())) {
        return jobOrder?.client_signature_uploaded_at || "";
      }

      const year = base.getUTCFullYear();
      const month = base.getUTCMonth();
      const date = base.getUTCDate();

      const rebuilt = new Date(Date.UTC(year, month, date, hour - 8, minute, 0));
      return rebuilt.toISOString();
    } catch {
      return jobOrder?.client_signature_uploaded_at || "";
    }
  };

  // Save function
  const handleSave = async () => {
    try {
      setIsLoading(true);

      const form = getValues();

      const inspection_report = (form.locations || []).map((r) => {
        const resolvedLocation =
          r.location === "Other"
            ? (r.location_other || "").trim()
            : (r.location || "").trim();

        return {
          location: resolvedLocation,
          location_other: r.location === "Other" ? (r.location_other || "").trim() : "",
          findings: (r.findings || "").trim(),
          action_taken: encodeActionTaken(r),
          remarks: (r.remarks || "").trim(),
        };
      });

      const recommendations = form.termite_recommendations?.length
        ? form.termite_recommendations
        : (jobOrder.termite_recommendations || []);

      const updatedJobOrderParameters = {
        ...jobOrder,
        inspection_report,
        exterra_bait_stations: form.exterra_bait_stations || [],
        termite_recommendations: recommendations,
        service_report_payment_status,
        service_report_payment_method,
        end_time: jobOrder?.end_time || "",
        ...(usesClientSignatureAsEndTime
          ? {
              client_signature_uploaded_at:
                jobOrder?.client_signature_uploaded_at || "",
            }
          : {}),
      };

      const payload = convertBooleans({
        jobOrder,
        updatedJobOrderParameters,
        form: updatedJobOrderParameters,
      });

      await submitDocumentFieldMapper(payload);

      const newLinks = buildGeneratedDocumentLinks(jobOrder.id, {
        includeReport: false,
        includeCertificate: false,
        includeReceipt: true,
      });

      await patchJobOrderLink(jobOrder.id, newLinks);
      await putJobOrder(jobOrder.id, {
        ...updatedJobOrderParameters,
        ...newLinks,
      });

      setJobOrder((prev) => ({
        ...prev,
        ...updatedJobOrderParameters,
        ...newLinks,
      }));

      if (sendEmailAfterEdit) {
        await handleSendEmail(newLinks);
        setSuccessMessage("Service report updated, documents regenerated, and email sent successfully!");
      } else {
        setSuccessMessage("Service report updated and documents regenerated successfully.");
      }
    } catch (err) {
      setErrorMessage("Error saving job order: " + (err.message || err));
    } finally {
      setIsLoading(false);
    }
  };


  // Recursively convert JS booleans to "True"/"False" strings for the PDF generator
  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) return obj.map(convertBooleans);
    if (obj && typeof obj === "object") {
      return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, convertBooleans(v)]));
    }
    if (typeof obj === "boolean") return obj ? "True" : "False";
    return obj;
  };

  const formatDate = (dateString) => {
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return "Invalid Date";
      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(d);
    } catch {
      return "Invalid Date";
    }
  };

  const handleSendEmail = async (attachmentLinksOverride) => {
    const recipientEmail =
      jobOrder?.client?.contact?.email?.email ||
      client?.contact?.email?.email ||
      "";

    if (!recipientEmail) {
      setErrorMessage("No client email found for this job order.");
      return;
    }

    const attachmentLinks =
      attachmentLinksOverride ||
      buildGeneratedDocumentLinks(jobOrder?.id, {
        includeReport: false,
        includeCertificate: false,
        includeReceipt: true,
      });

    const htmlBody = buildServiceReportEmail({
      formattedDate: formatDate(jobOrder?.job_date),
      attachmentLinks,
    });

    const lambdaPayload = {
      from: "boaz-reminders@maestrosystemsinc.com",
      recipients: [recipientEmail],
      subject: `Service Report for Job Order ${jobOrder?.id}`,
      body: htmlBody,
    };

    await sendEmailQueue(lambdaPayload);
  };

  const effectiveEndTime = getEffectiveEndTime(jobOrder);

  const displayJobOrder = {
    ...jobOrder,
    end_time: effectiveEndTime,
  };

  const usesClientSignatureAsEndTime = Boolean(jobOrder?.client_signature_uploaded_at);

  const pageOneJobOrder = {
    ...displayJobOrder,
    client: displayJobOrder?.client || client || {},
  };

  const STEP_LABELS = {
    review_job_details: "Review Job Details Completed At",
    inspection_report: "Inspection Report Completed At",
    service_details: "Service Details Completed At",
    payment_details: "Payment Details Completed At",
    technician_confirmation: "For Technicians Only Completed At",
    client_signature: "For Client Only - Signature Completed At",
    client_satisfaction: "For Client Only - Satisfaction Completed At",
  };

  const getLatestStepLogMap = (logs = []) => {
    return (Array.isArray(logs) ? logs : []).reduce((acc, log) => {
      const stepKey = log?.step_key;
      const timestamp = log?.timestamp;

      if (!stepKey || !timestamp) return acc;

      const prev = acc[stepKey];
      if (!prev || new Date(timestamp) > new Date(prev)) {
        acc[stepKey] = timestamp;
      }

      return acc;
    }, {});
  };

  const latestStepLogMap = getLatestStepLogMap(jobOrder?.report_page_logs);

  const getStepTimestamp = (stepKey) =>
    jobOrder?.[`${stepKey}_completed_at`] || latestStepLogMap?.[stepKey] || "";

  const documentLinks = {
    ...buildGeneratedDocumentLinks(jobOrder?.id, {
      includeReport: false,
      includeCertificate: false,
      includeReceipt: true,
    }),
    ...(jobOrder?.receipts_url && {
      receipts_url: jobOrder.receipts_url,
    }),
  };

  return (
    <FormProvider {...methods}>
      <BaseFormWrapper
        titleIcon={<ReceiptLongIcon />}
        title="Operations"
        subTitle="Service Reports"
        subTitleRoute="/manage-service-reports"
        formTitle="Edit Service Report"
        successMessage={successMessage}
        setSuccessMessage={setSuccessMessage}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        isLoading={isLoading}
      >
        <Grid container>
          <PageOneReviewSection
            jobOrder={pageOneJobOrder}
            client={client}
            selectedJobTypes={displayedJobTypes}
            selectedJobDescriptions={displayedJobDescriptions}
            jobOrderParameters={{ status: jobOrder?.status || "" }}
            errors={{}}
            onStatusChange={(value) =>
              setJobOrder((prev) => ({
                ...prev,
                status: value,
              }))
            }
            showStatus={true}
            readOnly={false}
          />

          {/* Structure Type Section (editable) */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="subtitle2">Structure Type</Typography>}
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <FormGroup sx={{ width: "100%" }}>
                  {STRUCTURE_TYPE_OPTIONS.map((option) => {
                    const isChecked = isTrue(jobOrder?.structure_type?.[option.key]) || false;
                    console.log(`Structure Type ${option.key}:`, jobOrder?.structure_type?.[option.key], 'isChecked:', isChecked);
                    
                    return (
                      <FormControlLabel
                        key={option.key}
                        sx={{ width: "100%" }}
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                              console.log(`Changing ${option.key} to:`, e.target.checked);
                              const currentStructureType = jobOrder?.structure_type || {};
                              const updatedStructureType = {
                                ...currentStructureType,
                                [option.key]: e.target.checked
                              };
                              console.log('Updated structure type:', updatedStructureType);
                              handleInputChange('structure_type', updatedStructureType);
                            }}
                          />
                        }
                        label={option.label}
                      />
                    );
                  })}
                </FormGroup>
              </CardContent>
            </Card>
          </Grid>

          {/* Structure Details (editable) */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    Structure Details
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Number of Bedrooms"
                      type="number"
                      value={sd?.bedrooms || ""}
                      fullWidth
                      size="small"
                      onChange={(e) => {
                        const currentDetails = jobOrder?.structure_details || {};
                        const updatedDetails = {
                          ...currentDetails,
                          bedrooms: e.target.value
                        };
                        handleInputChange('structure_details', updatedDetails);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Number of Restrooms"
                      type="number"
                      value={sd?.restrooms || ""}
                      fullWidth
                      size="small"
                      onChange={(e) => {
                        const currentDetails = jobOrder?.structure_details || {};
                        const updatedDetails = {
                          ...currentDetails,
                          restrooms: e.target.value
                        };
                        handleInputChange('structure_details', updatedDetails);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Number of Floors"
                      type="number"
                      value={sd?.floors || ""}
                      fullWidth
                      size="small"
                      onChange={(e) => {
                        const currentDetails = jobOrder?.structure_details || {};
                        const updatedDetails = {
                          ...currentDetails,
                          floors: e.target.value
                        };
                        handleInputChange('structure_details', updatedDetails);
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Termite Activity (view-only) */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    Termite Activity
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <FormGroup sx={{ width: "100%" }}>
                  <FormControlLabel
                    sx={{
                      width: "100%",
                      opacity: ta === "with" ? 1 : 0.75,
                    }}
                    control={
                      <Checkbox
                        checked={ta === "with"}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleInputChange('termite_activity', 'with');
                          } else if (ta === "with") {
                            handleInputChange('termite_activity', '');
                          }
                        }}
                      />
                    }
                    label="With Active Termites"
                  />

                  <FormControlLabel
                    sx={{
                      width: "100%",
                      opacity: ta === "without" ? 1 : 0.75,
                    }}
                    control={
                      <Checkbox
                        checked={ta === "without"}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleInputChange('termite_activity', 'without');
                          } else if (ta === "without") {
                            handleInputChange('termite_activity', '');
                          }
                        }}
                      />
                    }
                    label="Without Active Termites"
                  />
                </FormGroup>

                {ta !== "with" && ta !== "without" && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No selection
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Inspection & Pest Count (Mobile) — editable, wrapped in a parent card */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    Inspection Report
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <Stack spacing={2}>
                  {locationFields.map((loc, idx) => (
                    <TermiteLocationCard
                      key={loc.id}
                      control={control}
                      register={register}
                      index={idx}
                      removeLocation={removeLocation}
                    />
                  ))}
                  <Button
                    variant="contained"
                    disabled={locationFields.length >= 19}
                    fullWidth
                    onClick={() =>
                      appendLocation({
                        location: "",
                        location_other: "",
                        findings: "",
                        action_taken: "",
                        bait_monitor_location: "",
                        remarks: "",
                      })
                    }
                  >
                    Add New Location ({locationFields.length}/19)
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {!isTechnician && (
            <Grid item xs={12}>
              <SectionTimestampNotice
                label={STEP_LABELS.inspection_report}
                value={getStepTimestamp("inspection_report")}
                colorSet="info"
              />
            </Grid>
          )}

          {/* Recommendations Section - Editable */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2, mt: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    Recommendations
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                {(() => {
                  const RECOMMENDATIONS = [
                    "Schedule termite control",
                    "Monitor regularly",
                    "Seal entry points with caulking or foam",
                    "Improve airflow, reduce moisture",
                    "Repair leaks, dry affected zones",
                    "Stagnant water / leaking pipes",
                    "Relocate or elevate storage",
                    "Closed room or area Inaccessible",
                    "Ensure room access for full inspection",
                  ];

                  const currentRecommendations = getValues("termite_recommendations") ?? [];

                  return (
                    <Stack spacing={1}>
                      {RECOMMENDATIONS.map((label) => {
                        const isChecked = currentRecommendations.includes(label);
                        return (
                          <FormControlLabel
                            key={label}
                            sx={{ width: "100%" }}
                            control={
                              <Checkbox
                                checked={isChecked}
                                onChange={(e) => {
                                  const updatedRecommendations = e.target.checked
                                    ? [...currentRecommendations, label]
                                    : currentRecommendations.filter((v) => v !== label);
                                  
                                  setJobOrder(prev => ({
                                    ...prev,
                                    termite_recommendations: updatedRecommendations
                                  }));
                                  setValue("termite_recommendations", updatedRecommendations, { shouldDirty: true });
                                }}
                              />
                            }
                            label={label}
                          />
                        );
                      })}
                    </Stack>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          {/* Exterra Section - Editable */}
          <Grid item xs={12}>
            <ExterraCard control={control} register={register} />
          </Grid>

          {/* Chemical Used Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                <Grid item xs={12}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            align="center"
                            colSpan={2}
                            sx={{ fontWeight: "bold" }}
                          >
                            Chemical Used / Batch # / Expiration
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: "bold" }}
                          >
                            Dilution
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={2}
                            sx={{ fontWeight: "bold" }}
                          >
                            Equipment Used
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(jobOrder.chemical_used || []).map((chem, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{chem.label}:</TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={chem.chemical || ""}
                                onChange={(e) => {
                                  const updatedChemicals = [...(jobOrder.chemical_used || [])];
                                  updatedChemicals[idx] = { ...updatedChemicals[idx], chemical: e.target.value };
                                  handleInputChange('chemical_used', updatedChemicals);
                                }}
                                sx={{ minWidth: 100 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={chem.dilution || ""}
                                onChange={(e) => {
                                  const updatedChemicals = [...(jobOrder.chemical_used || [])];
                                  updatedChemicals[idx] = { ...updatedChemicals[idx], dilution: e.target.value };
                                  handleInputChange('chemical_used', updatedChemicals);
                                }}
                                sx={{ minWidth: 50 }}
                              />
                            </TableCell>
                            {jobOrder.equipment_used?.length > idx ? (
                              <>
                                <TableCell>
                                  {jobOrder.equipment_used[idx].label}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={jobOrder.equipment_used[idx].usage}
                                    onChange={(e) => {
                                      const updatedEquipment = [...(jobOrder.equipment_used || [])];
                                      updatedEquipment[idx] = { ...updatedEquipment[idx], usage: e.target.checked };
                                      handleInputChange('equipment_used', updatedEquipment);
                                    }}
                                  />
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell />
                                <TableCell />
                              </>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Reminder Notes Section */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                  >
                    Reminder Notes
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <TextField
                  label="Reminder Notes"
                  value={jobOrder.reminder_notes || ""}
                  fullWidth
                  multiline
                  rows={6}
                  size="small"
                  onChange={(e) => handleInputChange('reminder_notes', e.target.value)}
                />
              </CardContent>
            </Card>
          </Grid>

          {!isTechnician && (
            <Grid item xs={12}>
              <SectionTimestampNotice
                label={STEP_LABELS.service_details}
                value={getStepTimestamp("service_details")}
                colorSet="info"
              />
            </Grid>
          )}

          {/* Payment Section */}
          <PaymentDetailsSection
            serviceFee={jobOrder?.service_fee || ""}
            onServiceFeeChange={(value) =>
              setJobOrder((prev) => ({
                ...prev,
                service_fee: value,
              }))
            }
            paymentStatusOptions={service_report_payment_status}
            paymentMethodOptions={service_report_payment_method}
            onPaymentStatusChange={handlePaymentStatusChange}
            onPaymentMethodChange={handlePaymentMethodChange}
            readOnly={false}
          />

          {!isTechnician && (
            <Grid item xs={12}>
              <SectionTimestampNotice
                label={STEP_LABELS.payment_details}
                value={getStepTimestamp("payment_details")}
                colorSet="info"
              />
            </Grid>
          )}

          {/* Technician Only Section */}
          <TechnicianOnlyReviewSection
            jobOrder={displayJobOrder}
            isTechnician={isTechnician}
            editableSchedule={true}
            onStartTimeChange={(value) =>
              setJobOrder((prev) => ({
                ...prev,
                start_time: value,
              }))
            }
            onEndTimeChange={(value) =>
              setJobOrder((prev) => ({
                ...prev,
                end_time: value,
                ...(usesClientSignatureAsEndTime
                  ? {
                      client_signature_uploaded_at:
                        buildClientSignatureIsoFromEditedTime(value),
                    }
                  : {}),
              }))
            }
          />

          {!isTechnician && (
            <Grid item xs={12}>
              <SectionTimestampNotice
                label={STEP_LABELS.technician_confirmation}
                value={getStepTimestamp("technician_confirmation")}
                colorSet="info"
              />
            </Grid>
          )}
        
          {/* Client Only Section */}
          <ClientOnlyReviewSection
            jobOrder={displayJobOrder}
            isTechnician={isTechnician}
          />

          {/* Document Links Section */}
          <DocumentLinksCard
            title="Generated Documents"
            links={documentLinks}
          />

          {/* ⏰ TIMESTAMP DISPLAY - Report Submission (HIDDEN FROM TECHNICIANS) */}
          {!isTechnician && (
            <Grid item xs={12}>
              <SectionTimestampNotice
                label="Report Submitted At"
                value={jobOrder?.report_submitted_at}
                colorSet="warning"
              />
            </Grid>
          )}

          {/* Error Message Display */}
          {errorMessage && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            </Grid>
          )}

          {/* Action Buttons Section */}
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2}
            mb={2}
            gap={2}
          >
            <FormControlLabel
              sx={{
                mr: 2,
                ".MuiFormControlLabel-label": {
                  fontSize: "0.875rem",
                },
              }}
              control={
                <Checkbox
                  checked={sendEmailAfterEdit}
                  onChange={(e) => setSendEmailAfterEdit(e.target.checked)}
                  disabled={isLoading}
                />
              }
              label="Send email after save"
            />

            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                if (returnTo) {
                  window.location.href = returnTo;
                } else {
                  navigate("/manage-service-reports");
                }
              }}
              sx={{
                fontSize: "0.875rem",
                color: "#FF4C4C",
                borderColor: "#FF4C4C",
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
              disabled={isLoading}
            >
              {sendEmailAfterEdit ? "Save & Send Email" : "Save Changes"}
            </Button>
          </Grid>
        </Grid>
      </BaseFormWrapper>
    </FormProvider>
  );
}

export default EditTermiteControlForm;
